import React, { useState, useEffect, useRef } from "react";
import { Menu, ChevronDown, User, LogOut, Settings, MessageSquare } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePhoneAccount } from "../../contexts/PhoneAccountContext";
import SubscriptionStatus from "../SubscriptionStatus";
import { logout, isSuperAdmin } from "../../utils/auth";
import { getWallet } from "../../apis/wallet";
import useSubscriptionAccess from "../../hooks/useSubscriptionAccess";
import { fmtINR } from "../../utils/money";
import { IconSearch, IconWallet, IconBell, IconHelp } from "../../ui/ping/icons";

const Header = ({ toggleSidebar }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState("User");
  const [userInitial, setUserInitial] = useState("U");
  const [pageTitle, setPageTitle] = useState("Dashboard");
  const [pageSubtitle, setPageSubtitle] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [wallet, setWallet] = useState(null);
  const dropdownRef = useRef(null);

  // Get phone accounts for Connect WhatsApp button visibility
  const { 
    phoneAccounts: wabaAccounts, 
    fetchPhoneAccounts: fetchContextPhoneAccounts
  } = usePhoneAccount();
  const { loading: accessLoading, hasAccess, numberLimit } = useSubscriptionAccess();
  const atNumberCap = Boolean(numberLimit && !numberLimit.canAdd);

  const location = useLocation();
  const navigate = useNavigate();

  // Update page title
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("dashboard")) {
      setPageTitle("Dashboard");
      setPageSubtitle("Overview of your messaging analytics");
    } else if (path.includes("waba")) {
      setPageTitle("Whatsapp Numbers");
      setPageSubtitle("Manage your WhatsApp Numbers");
    } else if (path.includes("templates")) {
      setPageTitle("Templates");
      setPageSubtitle("Create and manage your WhatsApp message templates");
    } else if (path.includes("broadcast")) {
      setPageTitle("Campaigns");
      setPageSubtitle("Manage and track your WhatsApp campaigns");
    } else if (path.match(/\/campaigns\/\d+\/analytics/)) {
      setPageTitle("Campaign Analytics");
      setPageSubtitle("Delivery performance and contact breakdown");
    } else if (path.includes("campaigns")) {
      setPageTitle("Campaigns");
      setPageSubtitle("Manage and track your WhatsApp campaigns");
    } else if (path.includes("flow-list")) {
      setPageTitle("Flow Lists");
      setPageSubtitle("Manage your automation flows");
    } else if (path.includes("contacts") || path.includes("contact-lists")) {
      setPageTitle("Contacts");
      setPageSubtitle("Centralized contact universe for each WhatsApp phone");
    } else if (path.includes("live-chats")) {
      setPageTitle("Live Chats");
      setPageSubtitle("Manage real-time conversations with your customers");
    } else if (path.includes("download-center")) {
      setPageTitle("Download Center");
      setPageSubtitle("View and download your campaign log files");
    } else if (path.includes("profile")) {
      setPageTitle("Your Profile");
      setPageSubtitle("Manage your account settings");
    } else if (path.includes("settings")) {
      setPageTitle("Settings");
      setPageSubtitle("Configure your preferences");
    } else {
      setPageTitle("Dashboard");
      setPageSubtitle("");
    }
  }, [location]);

  useEffect(() => {
    // Try to get user name from multiple sources
    let name = null;
    
    // 1. First try localStorage "user" object
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.name) {
          name = parsedUser.name;
        }
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
    
    // 2. If no name found, try to decode from JWT token
    if (!name) {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.name) {
            name = payload.name;
          } else if (payload.email) {
            // Fallback to email if no name
            name = payload.email.split('@')[0];
          }
        } catch (e) {
          console.error("Error decoding token:", e);
        }
      }
    }
    
    // 3. Set the name if found
    if (name) {
      setUserName(name);
      setUserInitial(name.charAt(0).toUpperCase());
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin()) return undefined;
    let cancelled = false;
    getWallet()
      .then((res) => {
        if (!cancelled && res.data?.success) setWallet(res.data.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const toggleUserMenu = () => {
    setShowUserMenu((v) => !v);
  };

  const handleLogout = () => {
    logout();
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.location.reload();
    setTimeout(() => setIsRefreshing(false), 2000);
  };

  // Update WhatsApp connection status based on phone accounts from context
  useEffect(() => {
    setWhatsappConnected(wabaAccounts.length > 0);
  }, [wabaAccounts]);

  const checkWhatsappConnection = async () => {
    if (fetchContextPhoneAccounts) {
      await fetchContextPhoneAccounts();
    }
  };

  // Never launch Facebook signup from the header. The Phone Numbers page
  // owns connect + the extra-number paywall (cycle cap is not "currently connected").
  const handleConnectWhatsApp = () => {
    if (!accessLoading && !hasAccess) {
      navigate("/subscribe");
      return;
    }
    navigate("/waba?connect=1");
  };

  useEffect(() => {
    checkWhatsappConnection();

    const handleAccountUpdate = () => {
      if (fetchContextPhoneAccounts) {
        fetchContextPhoneAccounts();
      }
    };

    window.addEventListener("phoneAccountsUpdated", handleAccountUpdate);
    window.addEventListener("wabaAccountsUpdated", handleAccountUpdate);

    return () => {
      window.removeEventListener("phoneAccountsUpdated", handleAccountUpdate);
      window.removeEventListener("wabaAccountsUpdated", handleAccountUpdate);
    };
  }, [fetchContextPhoneAccounts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu]);

  return (
    <header className="ping-topbar">
      <button
        type="button"
        onClick={toggleSidebar}
        className="ping-icon-btn ping-menu-btn"
        aria-label="Toggle menu"
      >
        <Menu className="w-5 h-5" />
      </button>
      <div className="ping-search" aria-hidden>
        <IconSearch />
        Search contacts, templates, campaigns
      </div>
      <div className="grow" />
      <SubscriptionStatus />
      {!whatsappConnected && (
        <button
          type="button"
          onClick={handleConnectWhatsApp}
          className="ping-btn ping-btn-primary ping-btn-sm"
          title={atNumberCap ? "Buy an extra WhatsApp number" : "Connect WhatsApp Business"}
        >
          <MessageSquare size={14} /> {atNumberCap ? "Buy extra number" : "Connect WhatsApp"}
        </button>
      )}
      {isSuperAdmin() && wallet && location.pathname !== "/wallet" && (
        <button
          type="button"
          className="ping-wallet-chip"
          onClick={() => navigate("/wallet")}
        >
          <IconWallet />
          {fmtINR(wallet.balance)}
          <span className="add">+</span>
        </button>
      )}
      <button type="button" className="ping-icon-btn" title="Notifications" aria-label="Notifications">
        <IconBell />
      </button>
      <button
        type="button"
        className="ping-icon-btn"
        title="Help"
        aria-label="Help"
        onClick={() => navigate("/settings")}
      >
        <IconHelp />
      </button>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={toggleUserMenu}
          className="ping-icon-btn"
          aria-haspopup="menu"
          aria-expanded={showUserMenu}
          title={userName}
        >
          <span style={{ fontWeight: 700, fontSize: 13 }}>{userInitial}</span>
        </button>
        {showUserMenu && (
          <div
            role="menu"
            className="absolute right-0 mt-2 w-52 rounded-xl border shadow-lg overflow-hidden bg-white"
            style={{ borderColor: "var(--line)", zIndex: 50 }}
          >
            <div className="px-4 py-3 border-b" style={{ borderColor: "var(--line-soft)" }}>
              <p className="text-sm font-bold truncate">{userName}</p>
            </div>
            <button
              type="button"
              onClick={() => { navigate("/profile"); setShowUserMenu(false); }}
              className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-[#F0F2F5]"
            >
              <User className="w-4 h-4" /> Your Profile
            </button>
            <button
              type="button"
              onClick={() => { navigate("/settings"); setShowUserMenu(false); }}
              className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-[#F0F2F5]"
            >
              <Settings className="w-4 h-4" /> Settings
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-[#E41E3F] hover:bg-[#FDECEF]"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
